


















































































































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import * as PIXI from 'pixi.js';
import { DateTime } from 'luxon';
import { throttle } from 'throttle-typescript';
import { PixiPulldown } from '@/lib/pixiPulldown';
import { PixiRadioButton } from '@/lib/pixlRaddioButton';
import { PixiChart } from '@/lib/pixiChart';
import { getData, getInfo, putComment } from '@/api/data';
import { RRController } from '@/lib/rrController';
import { calcHeartRate, FloadReader, HeartRateParam } from '@/lib/heartRate';
import { MeasureDataType } from '../../../common/dist/common';
import { dashLine, rgb } from '../lib/common';
import PixiRrLine from '../lib/pixiRrLine';
import { getRr } from '../api/setting';
import { getDataCache, prefetch } from '../lib/dataCache';

type Point = [number, number];

interface RepeatLine {
  from: Point;
  to: Point;
  incremtnt: Point;
  count: number;
}

interface EcgArea {
  box: Point[];
  offset: Point;
  dashX: RepeatLine;
  dashY: RepeatLine;
  lineX: RepeatLine;
  lineY: RepeatLine;
  title: string;
  isPlusMinus: boolean;
}

const ecgM: EcgArea = {
  isPlusMinus: true,
  title: 'M-ECG',
  box: [
    [0, 0],
    [1260, 0],
    [1260, 210],
    [0, 210],
    [0, 0],
  ],

  dashY: {
    from: [21, 0],
    to: [21, 210],
    incremtnt: [42, 0],
    count: 30,
  },
  dashX: {
    from: [0, 42],
    to: [1260, 42],
    incremtnt: [0, 42],
    count: 4,
  },
  lineY: {
    from: [42, 0],
    to: [42, 210],
    incremtnt: [42, 0],
    count: 29,
  },
  lineX: {
    from: [0, 21],
    to: [1260, 21],
    incremtnt: [0, 42],
    count: 5,
  },
  offset: [30, 30],
};

const ecgF: EcgArea = {
  isPlusMinus: true,
  title: 'F-ECG',
  box: [
    [0, 0],
    [1260, 0],
    [1260, 210],
    [0, 210],
    [0, 0],
  ],

  dashY: {
    from: [21, 0],
    to: [21, 210],
    incremtnt: [42, 0],
    count: 30,
  },
  dashX: {
    from: [0, 42],
    to: [1260, 42],
    incremtnt: [0, 42],
    count: 4,
  },
  lineY: {
    from: [42, 0],
    to: [42, 210],
    incremtnt: [42, 0],
    count: 29,
  },
  lineX: {
    from: [0, 21],
    to: [1260, 21],
    incremtnt: [0, 42],
    count: 5,
  },
  offset: [30, 280],
};

const ctg: EcgArea = {
  isPlusMinus: false,
  title: 'CTG',
  box: [
    [0, 0],
    [1260, 0],
    [1260, 336],
    [0, 336],
    [0, 0],
  ],

  dashY: {
    from: [42, 0],
    to: [42, 336],
    incremtnt: [42, 0],
    count: 30,
  },
  dashX: {
    from: [0, 10.5],
    to: [1260, 10.5],
    incremtnt: [0, 10.5],
    count: 32,
  },
  lineY: {
    from: [42, 0],
    to: [42, 210],
    incremtnt: [42, 0],
    count: 0,
  },
  lineX: {
    from: [0, 21],
    to: [1260, 21],
    incremtnt: [0, 42],
    count: 8,
  },
  offset: [30, 530],
};

const LineColor = rgb(255, 255, 153);

function darwLines(g: PIXI.Graphics, r: RepeatLine, offset: Point) {
  const [fromX, fromY] = r.from;
  const [toX, toY] = r.to;
  const [offsetX, offsetY] = offset;
  const [incX, incY] = r.incremtnt;

  g.lineStyle(1, LineColor);
  for (let i = 0; i < r.count; i += 1) {
    g.moveTo(
      Math.floor(fromX + offsetX + incX * i),
      Math.floor(fromY + offsetY + incY * i),
    );
    g.lineTo(
      Math.floor(toX + offsetX + incX * i),
      Math.floor(toY + offsetY + incY * i),
    );
  }
}

function drawDashLines(g: PIXI.Graphics, r: RepeatLine, offset: Point) {
  const [fromX, fromY] = r.from;
  const [toX, toY] = r.to;
  const [offsetX, offsetY] = offset;
  const [incX, incY] = r.incremtnt;

  g.lineStyle(1, LineColor);
  for (let i = 0; i < r.count; i += 1) {
    dashLine(
      g,
      Math.floor(fromX + offsetX + incX * i),
      Math.floor(fromY + offsetY + incY * i),
      Math.floor(toX + offsetX + incX * i),
      Math.floor(toY + offsetY + incY * i),
      2,
      4,
    );
  }
}

function drawFrame(g: PIXI.Graphics, area: EcgArea) {
  g.lineStyle(2, LineColor);

  // darw outer line
  area.box.forEach((v, i) => {
    if (i === 0) {
      g.moveTo(v[0] + area.offset[0], v[1] + area.offset[1]);
    } else {
      g.lineTo(v[0] + area.offset[0], v[1] + area.offset[1]);
    }
  });

  drawDashLines(g, area.dashX, area.offset);
  drawDashLines(g, area.dashY, area.offset);
  darwLines(g, area.lineX, area.offset);
  darwLines(g, area.lineY, area.offset);

  const style = new PIXI.TextStyle({
    fontFamily: 'Arial',
    fill: [rgb(99, 255, 255)],
    fontSize: 20,
  });
  const text = new PIXI.Text(area.title, style);
  text.position = new PIXI.Point(area.offset[0], area.offset[1] - 26);

  if (area.isPlusMinus) {
    const plus = new PIXI.Text('+', style);
    plus.position = new PIXI.Point(area.offset[0] - 20, area.offset[1] + 5);

    const minus = new PIXI.Text('-', style);
    minus.position = new PIXI.Point(area.offset[0] - 20, area.offset[1] + 185);
    g.addChild(plus);
    g.addChild(minus);
  }

  g.addChild(text);
}

interface MinMax {
  min: number;
  max: number;
}
const minMax: MinMax[] = [
  {
    min: -5,
    max: 5,
  },
  {
    min: -2.5,
    max: 2.5,
  },
  {
    min: -1.25,
    max: 1.25,
  },
  {
    min: -0.126,
    max: 1.126,
  },
];

interface ZeroLineDef {
  line: [number, number][];
  text: string;
  textPos: number[];
  pos: number[];
}

const zeroLine: ZeroLineDef[] = [
  {
    line: [
      [0, 20],
      [5, 20],
      [5, 0],
      [13, 0],
      [13, 20],
      [18, 20],
    ],
    text: 'x1/2',
    textPos: [-10, 20],
    pos: [53, 113],
  },
  {
    line: [
      [0, 42],
      [5, 42],
      [5, 0],
      [13, 0],
      [13, 42],
      [18, 42],
    ],
    text: 'x1',
    textPos: [0, 40],
    pos: [53, 91],
  },
  {
    line: [
      [0, 84],
      [5, 84],
      [5, 0],
      [13, 0],
      [13, 84],
      [18, 84],
    ],
    text: 'x2',
    textPos: [0, 90],
    pos: [53, 49],
  },
  {
    line: [
      [0, 168],
      [5, 168],
      [5, 0],
      [13, 0],
      [13, 168],
      [18, 168],
    ],
    text: 'x4',
    textPos: [0, 170],
    pos: [53, 49],
  },
];

@Component
export default class Data extends Vue {
  private app: PIXI.Application = new PIXI.Application({
    width: 1420,
    height: 900,
    antialias: false,
    transparent: false,
    resolution: window.devicePixelRatio,
  });

  private chartContainer = new PIXI.Container();
  private ctgContainer = new PIXI.Container();

  private measureId = 0;
  private currentIndex = 0;
  private data?: Blob;

  private frame: PIXI.Graphics = new PIXI.Graphics();
  private pullDown: PixiPulldown = new PixiPulldown();
  private radio = new PixiRadioButton();
  private mRr = new RRController({
    t1: {
      min: 5,
      max: 600,
      step: 5,
      unit: 'msec',
    },
    t2: {
      min: 5,
      max: 3000,
      step: 25,
      unit: 'msec',
    },
    s: {
      min: 0.1,
      max: 5,
      step: 0.1,
      unit: 'mV',
    },
  });

  private fRr = new RRController({
    t1: {
      min: 5,
      max: 600,
      step: 5,
      unit: 'msec',
    },
    t2: {
      min: 5,
      max: 3000,
      step: 25,
      unit: 'msec',
    },
    s: {
      min: 5,
      max: 500,
      step: 5,
      unit: 'μV',
    },
  });

  private mRrLine = new PixiRrLine(1260, 210);
  private fRrLine = new PixiRrLine(1260, 210);

  private mecgChart = new PixiChart(1260, 210, 0xff4cde);
  private fecgChart = new PixiChart(1260, 210, 0xff4cde);
  private mCtgChart = new PixiChart(1260, 336, 0xff4cde);
  private fCtgChart = new PixiChart(1260, 336, 0x5dffff);

  private xMulti = 2;
  private minMax = 1;

  private zeroM = new PIXI.Graphics();
  private zeroF = new PIXI.Graphics();
  private info: Partial<MeasureDataType> = {};

  private pos = 0;
  private nextXMulti = 2;
  private nextMinMax = 1;

  private heartRateM: [number, number][] = [];
  private heartRateF: [number, number][] = [];

  private buffer?: DataView;
  public mRate = 0;
  public fRate = 0;
  private isRr = false;
  private mRrT = 1200;
  private fRrT = 1200;
  public time = 0;
  private firstLoad = true;

  private first = true;

  public dialog = false;
  public submitting = false;
  public comment = '';
  public success = false;

  public fast = false;
  public pause = false;
  public cursor = false;

  public cursorLine = new PIXI.Graphics();
  public cursorArea = new PIXI.Container();

  public loadDialog = new PIXI.Container();
  public loading = false;

  // 10分区切りページindex
  private pageIndex = 0;
  // 総ページ数(computedlen / 40)
  private totalPages = 0;
  // 1~40
  private currentPagewidth = 0;

  private heartRateParamM: HeartRateParam = {
    s: 0.5,
    scale: 1,
    t1: 60,
    t2: 300,
  };

  private heartRateParamF: HeartRateParam = {
    s: 50,
    scale: 1000,
    t1: 30,
    t2: 150,
  };

  @Prop({ default: 1 })
  public id!: number;

  destroyed() {
    console.log('unmount');
    this.app.destroy(true, {
      children: true,
      texture: true,
      baseTexture: true,
    });

    document.removeEventListener('visibilitychange', this.init);
  }

  mounted() {
    window.addEventListener('visibilitychange', this.init);
    if (!document.hidden) {
      this.init();
    }
  }

  public init() {
    if (!this.first) {
      return;
    }
    if (document.hidden) {
      return;
    }

    this.first = false;
    this.app.view.style.width = '1420px';
    this.app.view.ondblclick = (ev) => {
      ev.preventDefault();
    };
    this.app.view.onclick = (ev) => {
      ev.preventDefault();
    };
    this.app.view.onselectstart = (ev) => {
      ev.preventDefault();
    };
    PIXI.settings.RESOLUTION = window.devicePixelRatio;
    PIXI.settings.TARGET_FPMS = 0.005;

    const top = this.$refs.top as HTMLElement;
    top.appendChild(this.app.view);
    this.drawFrame();
    this.app.stage.addChild(this.frame);

    this.chartContainer.addChild(this.mecgChart.getContainer());
    this.mecgChart.getContainer().position = new PIXI.Point(...ecgM.offset);
    this.chartContainer.addChild(this.fecgChart.getContainer());
    this.fecgChart.getContainer().position = new PIXI.Point(...ecgF.offset);

    this.mCtgChart.getContainer().position = new PIXI.Point(...ctg.offset);
    this.ctgContainer.addChild(this.mCtgChart.getContainer());
    this.fCtgChart.getContainer().position = new PIXI.Point(...ctg.offset);
    this.ctgContainer.addChild(this.fCtgChart.getContainer());

    this.mRrLine.get().position = new PIXI.Point(...ecgM.offset);
    this.mRrLine.set(1000, 1100, 1200, 150);
    this.fRrLine.get().position = new PIXI.Point(...ecgF.offset);
    this.fRrLine.set(1000, 1100, 1200, 150);

    this.mRr.getContainer().visible = false;
    this.mRrLine.get().visible = false;
    this.fRr.getContainer().visible = false;
    this.fRrLine.get().visible = false;

    this.app.stage.addChild(this.chartContainer);
    this.app.stage.addChild(this.ctgContainer);
    this.app.stage.addChild(this.pullDown.getContainer());
    this.app.stage.addChild(this.radio.getContainer());
    this.app.stage.addChild(this.mRr.getContainer());
    this.app.stage.addChild(this.fRr.getContainer());
    this.app.stage.addChild(this.mRrLine.get());
    this.app.stage.addChild(this.fRrLine.get());
    this.mRr.set(this.heartRateParamM);
    this.mRr.getContainer().position = new PIXI.Point(1300, 120);
    this.fRr.set(this.heartRateParamF);
    this.fRr.getContainer().position = new PIXI.Point(1300, 279);

    this.pullDown.getContainer().position = new PIXI.Point(1171, 3);
    this.radio.getContainer().position = new PIXI.Point(1300, 29);

    this.app.stage.addChild(this.zeroM);
    this.app.stage.addChild(this.zeroF);

    this.cursorLine.visible = false;

    this.cursorArea.width = 1260;
    this.cursorArea.height = 336;
    this.cursorArea.position = new PIXI.Point(...ctg.offset);
    this.cursorArea.addChild(this.cursorLine);
    this.cursorLine.lineStyle(1, 0xff0707).moveTo(0, 0).lineTo(0, 336);
    this.app.stage.addChild(this.cursorArea);

    this.pullDown.on('select', (v) => {
      this.nextXMulti = v.index;
    });

    this.radio.on('select', (v) => {
      this.nextMinMax = v.index;
    });

    this.frame.cacheAsBitmap = true;

    this.app.ticker.remove(this.tick);
    this.app.ticker.add(this.tick);

    this.mRr.on('update', this.onParamUpdateM);
    this.mRr.on('tchange', this.onTChangeM);
    this.fRr.on('update', this.onParamUpdateF);
    this.fRr.on('tchange', this.onTChangeF);

    this.cursorArea.interactive = true;
    this.app.stage.interactive = true;

    const update = throttle(this.moveToCursor, 100);
    this.cursorArea.on('pointermove', (e: PIXI.InteractionEvent) => {
      const local = e.data.getLocalPosition(this.cursorArea);
      this.cursorLine.x = Math.max(0, Math.min(local.x, 1260));

      if (this.cursor) {
        update();
      }
    });

    this.cursorArea.hitArea = new PIXI.Rectangle(0, 0, 1260, 336);
    this.cursorArea.on('pointerdown', () => {
      /* 謎
      if (this.cursor) {
        this.moveToCursor();
        this.cursor = false;
        this.pause = false;
        this.cursorLine.visible = false;
      }
      */
    });

    const frame = new PIXI.Graphics();
    frame
      .beginFill(0)
      .lineStyle(2, 0xffffff)
      .drawRect(0, 0, 100, 30)
      .endFill();
    this.loadDialog.addChild(frame);
    this.app.stage.addChild(this.loadDialog);
    this.loadDialog.position = new PIXI.Point(1260 / 2 - 50, 400);
    const text = new PIXI.Text('Loading', new PIXI.TextStyle({
      stroke: 0xffffff,
      fill: 0xffffff,
      fontSize: 20,
    }));
    text.position = new PIXI.Point(12, 3);
    this.loadDialog.addChild(text);
    this.loadDialog.visible = false;

    this.load();
  }

  private getXMultiScale() {
    const scale = [0.5, 1, 2][this.xMulti];
    return scale;
  }

  private updateRate(index: number) {
    const mit = this.heartRateM.find((v) => v[0] >= index);
    if (mit) {
      this.$set(this, 'mRate', Math.floor(mit[1]));
    } else {
      this.$set(this, 'mRate', '-');
    }

    const fit = this.heartRateF.find((v) => v[0] >= index);
    if (fit) {
      this.$set(this, 'fRate', Math.floor(fit[1]));
    } else {
      this.$set(this, 'fRate', '-');
    }
  }

  private tick(delta: number) {
    if (this.loading) {
      return;
    }
    const scale = this.getXMultiScale();
    const step = (delta / (PIXI.settings.TARGET_FPMS * 1000)) * (42 * scale);
    this.pos += step;

    const time = delta / (PIXI.settings.TARGET_FPMS * 1000);
    if (!this.pause) {
      this.time = Math.min(30 / scale, this.time + time * (this.fast ? 5 : 1));
    }

    const hrIndex = this.currentIndex * 15000 + 1000 * this.time;
    this.updateRate(hrIndex);

    const checkPoint = [[315, 630, 945, 1260], [630, 1260], [1260]][
      this.xMulti
    ];
    const isEnd = checkPoint.some((v) => this.pos >= v && this.pos - step < v);
    if (isEnd) {
      if (this.nextMinMax !== this.minMax || this.nextXMulti !== this.xMulti) {
        this.xMulti = this.nextXMulti;
        this.minMax = this.nextMinMax;
        this.currentIndex = 0;
        this.start();
        return;
      }
    }

    if (this.time >= 30 / scale) {
      if (!this.nextPage()) {
        if (this.canFowerd10min()) {
          this.pageIndex += 1;
          this.load();
          return;
        }
        this.pause = true;
        this.app.stop();
      }

      this.mecgChart.tick(30 / scale, 30 / scale);
      this.fecgChart.tick(30 / scale, 30 / scale);

      this.mCtgChart.tick(30 / scale + this.currentIndex * 15, 600);
      this.fCtgChart.tick(30 / scale + this.currentIndex * 15, 600);
    } else {
      this.mecgChart.tick(this.time, 30 / scale);
      this.fecgChart.tick(this.time, 30 / scale);

      this.mCtgChart.tick(this.time + this.currentIndex * 15, 600);
      this.fCtgChart.tick(this.time + this.currentIndex * 15, 600);
    }
  }

  private nextPage(nums = 1) {
    const len = this.currentPagewidth;
    const step = [4, 2, 1][this.xMulti];
    let current = nums;

    while (current > 0) {
      const it = step * current;
      if (this.currentIndex + it < len) {
        this.currentIndex += it;
        this.start();
        return true;
      }
      current -= 1;
    }

    return false;
  }

  private async load(nextIndex = 0, nextTime = 0) {
    this.measureId = this.id;
    this.loadDialog.visible = true;
    this.loading = true;
    try {
      const rr = await getRr();
      if (rr.success && this.firstLoad) {
        this.mRr.setRr({ t1: rr.data.mT1, t2: rr.data.mT2, s: rr.data.mS });
        this.fRr.setRr({ t1: rr.data.fT1, t2: rr.data.fT2, s: rr.data.fS });
        this.firstLoad = false;
      }

      const info = await getInfo(this.measureId);
      if (info.success) {
        this.info = info.data;
        this.comment = info.data.comment || '';

        this.totalPages = Math.floor(info.data.computed.length / 40) +
          (info.data.computed.length % 40 > 0 ? 1 : 0);
        this.currentPagewidth = Math.min(40,
          (this.info.computed?.length || 0) - this.pageIndex * 40);
      }
      const cacheMode = localStorage.getItem('cache') === 'true';
      const data = cacheMode ?
        await getDataCache(this.measureId, this.pageIndex) :
        await getData(this.measureId, this.pageIndex);
      this.data = data;

      if (cacheMode) {
        prefetch(this.measureId, this.totalPages);
      }

      this.buffer = new DataView(await this.data.arrayBuffer());
      this.currentIndex = 0;
      this.start(nextIndex, nextTime);
    } catch (error) {
      console.log(error);
    }
    this.loadDialog.visible = false;
    this.loading = false;
  }

  private start(nextIndex = 0, nextTime = 0) {
    this.xMulti = this.nextXMulti;
    const scale = minMax[this.minMax];

    if (!this.buffer) {
      return;
    }
    this.heartRateM = calcHeartRate(
      new FloadReader(
        this.buffer,
        0,
        15000,
        15000,
        30000 * this.currentPagewidth,
      ),
      this.heartRateParamM,
    );
    this.heartRateF = calcHeartRate(
      new FloadReader(
        this.buffer,
        15000,
        15000,
        15000,
        30000 * this.currentPagewidth,
      ),
      this.heartRateParamF,
    );

    const average = [47, 23, 11][this.xMulti];
    const len = this.currentPagewidth;
    const mReader = new FloadReader(this.buffer, 0, 15000, 15000, len * 30000);
    const fReader = new FloadReader(
      this.buffer,
      15000,
      15000,
      15000,
      len * 30000,
    );
    if (nextIndex !== 0) {
      this.currentIndex = nextIndex;
    }
    mReader.skipFowerd(15000 * this.currentIndex);
    fReader.skipFowerd(15000 * this.currentIndex);

    this.mecgChart.draw(mReader, 1260, average, scale.min, scale.max);
    this.fecgChart.draw(fReader, 1260, average, scale.min, scale.max, -0.5);
    this.mCtgChart.draw2(this.heartRateM, 50, 210);
    this.fCtgChart.draw2(this.heartRateF, 50, 210);

    this.drawZero(this.zeroM, zeroLine[this.minMax]);
    this.drawZero(this.zeroF, zeroLine[this.minMax], 250);

    this.pos = 42;
    this.app.start();
    if (nextTime === 0) {
      this.time = 1 / this.getXMultiScale();
    } else {
      this.time = nextTime;
    }
    this.setMmLineM();
    this.setMmLineF();
  }

  // eslint-disable-next-line class-methods-use-this
  private drawZero(g: PIXI.Graphics, info: ZeroLineDef, offset = 0) {
    const style = new PIXI.TextStyle({
      fontFamily: 'Arial',
      fill: [rgb(99, 255, 255)],
      fontSize: 20,
    });
    g.clear();
    g.lineStyle(1, rgb(99, 255, 255));
    info.line.forEach((v, i) => {
      if (i === 0) {
        g.moveTo(...v);
        return;
      }
      g.lineTo(...v);
    });
    g.position = new PIXI.Point(info.pos[0], info.pos[1] + offset);

    g.removeChildren();
    const text = new PIXI.Text(info.text, style);
    g.addChild(text);
    text.position = new PIXI.Point(...info.textPos);
  }

  private drawFrame() {
    drawFrame(this.frame, ecgM);
    drawFrame(this.frame, ecgF);
    drawFrame(this.frame, ctg);

    const style = new PIXI.TextStyle({
      fill: [0x5dffff],
      fontSize: 13,
    });
    for (let i = 0; i < 2; i += 1) {
      for (let p = 0; p < 8; p += 1) {
        const text = new PIXI.Text(`${200 - p * 20}`, style);
        text.position = new PIXI.Point(
          ctg.offset[0] + 6 * 42 + i * (15 * 42) - 10,
          ctg.offset[1] + 42 * p + 15,
        );
        this.frame.addChild(text);
      }
    }
    for (let i = 0; i < 11; i += 1) {
      const text = new PIXI.Text(`${i}min`, style);
      text.position = new PIXI.Point(
        ctg.offset[0] + 3 + i * 42 * 3,
        ctg.offset[1] + 42 * 8,
      );
      this.frame.addChild(text);
    }
    this.frame.cacheAsBitmap = true;
  }

  public get position() {
    const len = this.info.computed?.length || 0;
    const date = DateTime.local(0, 1, 1, 0, 0, 0);
    return `${date
      .plus({ second: this.pageIndex * 600 + this.currentIndex * 15 + this.time })
      .toFormat('mm:ss')} / ${date
      .plus({ second: len * 15 })
      .toFormat('mm:ss')}`;
  }

  public onPrev() {
    this.xMulti = this.nextXMulti;
    this.minMax = this.nextMinMax;

    const skip = [4, 2, 1][this.xMulti];
    this.pos = 0;
    this.currentIndex = Math.max(0, this.currentIndex - skip);
    this.start();
  }

  public onNext() {
    this.xMulti = this.nextXMulti;
    this.minMax = this.nextMinMax;

    const len = this.currentPagewidth;
    const skip = [4, 2, 1][this.xMulti];
    this.pos = 0;
    this.currentIndex = Math.min(len - 1, this.currentIndex + skip);
    this.start();
  }

  public getDate() {
    if (!this.info || !this.info.date) {
      return '';
    }
    return DateTime.fromISO(this.info.date).toFormat('yyyy/LL/dd HH:mm:ss');
  }

  public getExpectionDate() {
    if (!this.info || !this.info.patient?.expectingDate) {
      return '';
    }
    return DateTime.fromISO(this.info.patient.expectingDate).toFormat(
      'yyyy/LL/dd',
    );
  }

  public getWeekDays() {
    if (!this.info || !this.info.patient?.expectingDate || !this.info.date) {
      return '';
    }
    const begin = DateTime.fromISO(this.info.patient.begin);
    const measuerDate = DateTime.fromISO(this.info.date);
    const diff = measuerDate.diff(begin, 'days').shiftTo('weeks', 'days');
    return `${Math.floor(diff.weeks)} / ${Math.floor(diff.days)}`;
  }

  public getComment() {
    return this.info?.comment || '';
  }

  public onTChangeM(val: number) {
    this.mRrT += val;
    this.setMmLineM();
  }

  public onTChangeF(val: number) {
    this.fRrT += val;
    this.setMmLineF();
  }

  public onParamUpdateM(v: HeartRateParam) {
    this.heartRateParamM = v;
    this.setMmLineM();
  }

  public onParamUpdateF(v: HeartRateParam) {
    this.heartRateParamF = v;
    this.setMmLineF();
  }

  private setMmLineM() {
    const scale = minMax[this.minMax];
    const msecPerX = 1260 / (30000 / this.getXMultiScale());
    const mvPerY = 210 / (scale.max - scale.min);
    const t = msecPerX * this.mRrT * 5;
    const t1 = msecPerX * this.heartRateParamM.t1 + t;
    const t2 = msecPerX * this.heartRateParamM.t2 + t;
    const s =
      210 -
      mvPerY *
        (this.heartRateParamM.s +
          (scale.max - scale.min) / 2 -
          Math.abs(Math.abs(scale.max) - Math.abs(scale.min)) / 2);
    this.mRrLine.set(t, t1, t2, s);
    this.mRr.set(this.heartRateParamM);
  }

  private setMmLineF() {
    const scale = minMax[this.minMax];
    const msecPerX = 1260 / (30000 / this.getXMultiScale());
    const mvPerY = 210 / (scale.max - scale.min);
    const t = msecPerX * this.fRrT * 5;
    const t1 = msecPerX * this.heartRateParamF.t1 + t;
    const t2 = msecPerX * this.heartRateParamF.t2 + t;
    const s =
      210 -
      mvPerY *
        (this.heartRateParamF.s / this.heartRateParamF.scale +
          (scale.max - scale.min) / 2 -
          Math.abs(Math.abs(scale.max) - Math.abs(scale.min)) / 2);

    this.fRrLine.set(t, t1, t2, s);
    this.fRr.set(this.heartRateParamF);
  }

  public onRr() {
    this.isRr = !this.isRr;

    if (this.isRr) {
      this.setMmLineM();
      this.setMmLineF();
    }

    if (!this.isRr) {
      this.start();
    }

    this.mRr.getContainer().visible = this.isRr;
    this.mRrLine.get().visible = this.isRr;
    this.fRr.getContainer().visible = this.isRr;
    this.fRrLine.get().visible = this.isRr;
  }

  public async saveComment() {
    if (!this.info || !this.info.id) {
      return;
    }
    this.submitting = true;

    const ret = await putComment(this.info.id, { str: this.comment });
    if (ret.success) {
      this.success = true;
    }

    this.submitting = false;
    this.dialog = false;
  }

  public onBackward() {
    if (this.checkRestart()) {
      return;
    }
    if (this.pageIndex !== 0) {
      this.pageIndex = 0;
      this.currentIndex = 0;
      this.load();
    } else {
      this.currentIndex = 0;
      this.start();
    }
  }

  private isChanged() {
    return this.nextXMulti !== this.xMulti || this.nextMinMax !== this.minMax;
  }

  private checkRestart() {
    if (!this.isChanged()) {
      return false;
    }
    this.currentIndex = 0;
    this.xMulti = this.nextXMulti;
    this.minMax = this.nextMinMax;
    this.start();
    return true;
  }

  public onPause() {
    if (this.checkRestart()) {
      return;
    }
    this.pause = !this.pause;
  }

  public onPlay() {
    this.pause = false;
    this.fast = false;
  }

  public onFast() {
    this.fast = !this.fast;
  }

  public onForward60() {
    this.onForward(60);
  }

  public onForward15() {
    this.onForward(15);
  }

  public onForward(times: number) {
    if (this.checkRestart()) {
      return;
    }

    const width = 30 / this.getXMultiScale();
    let remain = times;
    const prev = this.time;
    let page = 0;

    if (width - this.time < times) {
      page += 1;
      remain -= (width - this.time);
    }

    const nextPos = (prev + times) % width;

    const nums = Math.floor(remain / width) + page;
    if (nums > 0) {
      if (this.nextPage(nums)) {
        this.time = nextPos;
      } else {
        this.time = width;
      }
    } else {
      this.time = nextPos;
    }

    /*
    if (width - this.time < remain) {
      const pad = width - this.time;
      this.nextPage();
      this.time = 0;
      remain -= pad;
    }

    if (nums > 0) {
      if (this.nextPage(nums)) {
        remain -= (width * nums);
        this.time = 0;
      } else {
        this.time = (width * nums);
        return;
      }
    }
    this.time += remain;
    */
  }

  public onCursor() {
    this.cursor = !this.cursor;
    this.cursorLine.visible = this.cursor;
    if (this.cursor) {
      this.pause = true;
    }
  }

  private moveToCursor() {
    const { x } = this.cursorLine;
    const totalTimes = (this.currentPagewidth) * 15;
    const time = Math.min(totalTimes, (x / 1260) * (60 * 10));

    const width = [4, 2, 1][this.xMulti];
    const index = Math.floor(time / (15 * width)) * width;
    this.currentIndex = index;
    this.start();
    this.time = time - (index * 15);
  }

  public canRewind10min() {
    if (this.totalPages <= 1) {
      return false;
    }
    return this.pageIndex !== 0;
  }

  public canFowerd10min() {
    if (this.totalPages <= 1) {
      return false;
    }
    return this.pageIndex !== this.totalPages - 1;
  }

  public rewind10min() {
    this.pageIndex -= 1;
    this.load(this.currentIndex, this.time);
  }

  public fowerd10min() {
    this.pageIndex += 1;
    this.load(this.currentIndex, this.time);
  }
}
